import { useEffect, useState, type RefObject } from "react";
import { Search, PinOff } from "lucide-react";
import { motion, MotionConfig } from "motion/react";
import { OrderListItem, sortByPinned, type Order, type VolumeUnit } from "../../../vendor/prism-next/map/logistics/index.js";
import type { LogisticsMapViewHandle } from "@/features/map/logistics-map-view";
import { usePolledRef } from "@/features/map/use-polled-ref";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface OrderSidebarProps {
  orders: Order[];
  hoveredIndex: number | null;
  mapRef: RefObject<LogisticsMapViewHandle | null>;
  /** Environment volume-capacity unit — forwarded to each OrderListItem. */
  capacityUnit: VolumeUnit;
  /** Pinned orders by order_id in pin order — OrderOverlay's authoritative pin set
   *  (rc1.26's `onOrderPinnedChange`), mirrored down from the page. Orders have no
   *  on-map deck; pin just emphasises the marker + floats the row. */
  pinnedOrderIds: readonly string[];
}

function matchesQuery(o: Order, query: string): boolean {
  if (!query) return true;
  const hay = [o.order_ref, o.status, o.location.name, o.location.code, ...(o.labels ?? [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(query);
}

/** Right next to RouteSidebar — same search/hide/pin/toggle-all onto prism-next's
 *  <OrderListItem>. Pin state is owned by OrderOverlay (rc1.26 `onOrderPinnedChange`,
 *  passed in as `pinnedOrderIds`) and the pinned-first order uses `sortByPinned` —
 *  no local pin mirror. Orders have no pinned deck; they keep prism-next's hover popup. */
export function OrderSidebar({ orders, hoveredIndex, mapRef, capacityUnit, pinnedOrderIds }: OrderSidebarProps) {
  const [query, setQuery] = useState("");
  const [manualHidden, setManualHidden] = useState<Set<number>>(new Set());
  const overlay = usePolledRef(mapRef, (h) => h.orders);
  const pinned = new Set(pinnedOrderIds);

  useEffect(() => {
    setManualHidden(new Set());
    setQuery("");
  }, [orders]);

  const order = sortByPinned(
    orders.map((_, i) => i).filter((i) => matchesQuery(orders[i], query)),
    pinnedOrderIds,
    (i) => orders[i].order_id,
  );
  const orderSet = new Set(order);

  useEffect(() => {
    if (!overlay) return;
    orders.forEach((_, i) => overlay.setVisible(i, orderSet.has(i) && !manualHidden.has(i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, orders, query, manualHidden]);

  const shown = order.filter((i) => !manualHidden.has(i)).length;
  const anyVisibleMatching = order.some((i) => !manualHidden.has(i));

  const setHidden = (i: number, hidden: boolean): void => {
    setManualHidden((prev) => {
      const next = new Set(prev);
      if (hidden) next.add(i);
      else next.delete(i);
      return next;
    });
  };

  // Pin toggles just mutate the overlay; onOrderPinnedChange then updates
  // `pinnedOrderIds` (via the page), re-rendering this list.
  const setPinnedRow = (i: number, pin: boolean): void => {
    if (pin) {
      overlay?.pin(i);
      overlay?.focusOrder(i);
    } else {
      overlay?.unpin(i);
    }
  };

  const toggleAll = (): void => {
    setManualHidden(anyVisibleMatching ? new Set(order) : new Set());
  };

  return (
    <div className="flex h-full min-h-0 w-80 flex-col gap-2 border-r p-3">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={toggleAll} disabled={!order.length}>
          {anyVisibleMatching ? "Hide all" : "Show all"}
        </Button>
        <span className="text-xs text-muted-foreground">
          {shown}/{orders.length} shown
          {manualHidden.size ? ` · ${manualHidden.size} hidden` : ""}
        </span>
        {pinnedOrderIds.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Unpin all ${pinnedOrderIds.length} pinned`}
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => orders.forEach((o, i) => pinned.has(o.order_id) && overlay?.unpin(i))}
              >
                <PinOff />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Unpin all</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="relative">
        <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value.trim().toLowerCase())}
          placeholder="Search orders…"
          className="pl-8"
        />
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <MotionConfig reducedMotion="user">
        <div className="flex flex-col gap-1 pr-2">
          {!orders.length && <p className="p-4 text-center text-sm text-muted-foreground">No orders loaded.</p>}
          {orders.length > 0 && !order.length && (
            <p className="p-4 text-center text-sm text-muted-foreground">No matches.</p>
          )}
          {order.map((i) => {
            const o = orders[i];
            const visible = !manualHidden.has(i);
            const isPinned = pinned.has(o.order_id);
            const color = overlay?.orderColor(i) ?? "var(--muted-foreground)";
            return (
              // layout="position" FLIP-animates the row when pinning reshuffles the
              // order; MotionConfig honours prefers-reduced-motion.
              <motion.div key={o.order_id} layout="position" transition={{ duration: 0.2, ease: "easeOut" }}>
                <OrderListItem
                  order={o}
                  color={color}
                  visible={visible}
                  pinned={isPinned}
                  capacityUnit={capacityUnit}
                  onHoverChange={(hovered) => (hovered ? overlay?.hoverOrder(i) : overlay?.clearHover())}
                  onVisibleChange={(next) => setHidden(i, !next)}
                  onPinnedChange={(next) => setPinnedRow(i, next)}
                  className={hoveredIndex === i ? "bg-muted/50" : undefined}
                />
              </motion.div>
            );
          })}
        </div>
        </MotionConfig>
      </ScrollArea>
    </div>
  );
}
